import Button from './button';
import ButtonEvent from './button-event';
import theme from './theme';
import ToggleButtonEvent from './toggle-button-event';

/**
 * The ToggleButton acts like the Button component, but can be toggled as being selected or not.
 *
 * When the ToggleButton is clicked or Space / Enter is released while focused.
 *
 * The selected property is set to the inversed boolean value and a ToggleButtonEvent is dispatched.
 */
export default class ToggleButton extends Button {
	/**
	 * The selected property is set to true when the SelectableContainer is clicked or Space / Enter is released while focused.
	 *
	 * When the selected property is set from within the SelectableContainer, a SelectEvent is dispatched that bubbles up through the DOM tree.
	 *
	 * @param {boolean} value True if selected, false otherwise
	 */
	public set selected(value: boolean) {
		if (this.#selected === value) {
			return;
		}
		assertIsBoolean(value);
		this.#selected = value;
		this.#selectedChanged = true;
		this.invalidateProperties();
	}

	public get selected(): boolean {
		return this.#selected;
	}

	#selected: boolean;
	#selectedChanged: boolean;

	public constructor() {
		super();
		this.#selected = false;
		this.#selectedChanged = false;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#selectedChanged) {
			this.#commitSelected();
		}
	}

	protected override enabledChanged(): void {
		super.enabledChanged();
		this.#invalidateStyles();
	}

	protected override initialize(): void {
		super.initialize();
		this.addEventListener(ButtonEvent.CLICKED, this.#clicked);
	}

	protected override stateChanged(): void {
		super.stateChanged();
		this.#invalidateStyles();
	}

	#clicked(event: ButtonEvent): void {
		event.stopPropagation();
		this.selected = !this.selected;
		this.blur();
		this.#dispatchToggleButtonEvent();
	}

	#commitSelected(): void {
		this.#selectedChanged = false;
		this.#invalidateStyles();
	}

	#dispatchToggleButtonEvent(): void {
		this.dispatchEvent(new ToggleButtonEvent(this.selected));
	}

	#invalidateStyles(): void {
		if (this.selected) {
			this.#setSelectedStyles();
		} else {
			this.#setUnSelectedStyles();
		}
	}

	#setIdleStyles(): void {
		this.fill = theme.colors.fill.white;
		this.stroke = theme.strokes.soft;
	}

	#setSelectedIdleStyles(): void {
		this.fill = theme.colors.fill.weak;
		this.stroke = theme.strokes.sub;
	}

	/** Only the idle look tells selected from not, and it is the styled state that is asked: hovering is not styled, so a selected ToggleButton under the pointer still looks selected. */
	#setSelectedStyles(): void {
		if (this.styledState === 'idle') {
			this.#setSelectedIdleStyles();
		}
	}

	#setUnSelectedStyles(): void {
		if (this.styledState === 'idle') {
			this.#setIdleStyles();
		}
	}
}
customElements.define('ac-toggle-button', ToggleButton);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type boolean`);
}
